// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  MessageSquare,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  AIConfigurationStepDefinition,
  AIConfigurationStepId,
} from '../../configuration/steps.ts';
import './AIConfigurationUI.css';

export type ConfigurationStepStatus =
  | 'current'
  | 'completed'
  | 'pending'
  | 'attention'
  | 'published'
  | 'needs-publish';

export type ConfigurationNavigationStep = AIConfigurationStepDefinition & {
  status: ConfigurationStepStatus;
};

const STATUS_LABELS: Record<ConfigurationStepStatus, string> = {
  current: 'Atual',
  completed: 'Concluída',
  pending: 'Pendente',
  attention: 'Requer atenção',
  published: 'Publicado',
  'needs-publish': 'Pendente',
};

export const SaveStatus = ({
  state,
  savedAt,
}: {
  state: 'idle' | 'saving' | 'saved' | 'error';
  savedAt?: Date | null;
}) => {
  if (state === 'idle') return null;

  const label = state === 'saving'
    ? 'Salvando alterações…'
    : state === 'error'
      ? 'Erro ao salvar o rascunho'
      : savedAt
        ? 'Rascunho salvo há alguns segundos'
        : 'Rascunho salvo';

  return (
    <span
      className={`ai-save-status is-${state}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {state === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
      {label}
    </span>
  );
};

export const ConfigurationHeader = ({
  saveState,
  savedAt,
  onTest,
}: {
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  savedAt?: Date | null;
  onTest: () => void;
}) => (
  <header className="ai-configuration-header">
    <div className="ai-configuration-heading">
      <h1><Bot size={28} aria-hidden="true" />Configurações IA</h1>
      <p>Defina o que a IA faz, personalize o atendimento e publique suas configurações.</p>
    </div>
    <div className="ai-configuration-header-actions">
      <SaveStatus state={saveState} savedAt={savedAt} />
      <button type="button" className="ai-button is-test" onClick={onTest}>
        <MessageSquare size={19} /> Testar conversa
      </button>
    </div>
  </header>
);

const ConfigurationStepMarker = ({ status, index }: { status: ConfigurationStepStatus; index: number }) => {
  if (status === 'completed' || status === 'published') return <Check size={15} strokeWidth={3} />;
  if (status === 'attention' || status === 'needs-publish') return <AlertCircle size={16} />;
  return <span>{index + 1}</span>;
};

export const ConfigurationSidebar = ({
  steps,
  activeStep,
  onSelect,
}: {
  steps: ConfigurationNavigationStep[];
  activeStep: AIConfigurationStepId;
  onSelect: (id: AIConfigurationStepId) => void;
}) => {
  const activeIndex = steps.findIndex((step) => step.id === activeStep);
  const active = steps[activeIndex] || steps[0];

  return (
    <>
      <aside className="ai-steps-sidebar" aria-label="Etapas da configuração">
        <ol>
          {steps.map((step, index) => (
            <li key={step.id}>
              <button
                type="button"
                className={`ai-step-item is-${step.status}`}
                aria-current={step.id === activeStep ? 'step' : undefined}
                onClick={() => onSelect(step.id)}
              >
                <span className="ai-step-marker" aria-hidden="true">
                  <ConfigurationStepMarker status={step.status} index={index} />
                </span>
                <span className="ai-step-copy">
                  <strong>{step.title}</strong>
                  <small>{STATUS_LABELS[step.status]}</small>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </aside>
      <label className="ai-mobile-step-selector">
        <span>Etapa {activeIndex + 1} de {steps.length}: {active.title}</span>
        <span className="ai-mobile-step-select-wrap">
          <select value={activeStep} onChange={(event) => onSelect(event.target.value as AIConfigurationStepId)}>
            {steps.map((step, index) => (
              <option key={step.id} value={step.id}>
                {index + 1}. {step.title} ({STATUS_LABELS[step.status]})
              </option>
            ))}
          </select>
          <ChevronDown size={17} aria-hidden="true" />
        </span>
      </label>
    </>
  );
};

export const AIConfigurationLayout = ({
  steps,
  activeStep,
  onSelectStep,
  children,
}: {
  steps: ConfigurationNavigationStep[];
  activeStep: AIConfigurationStepId;
  onSelectStep: (id: AIConfigurationStepId) => void;
  children: ReactNode;
}) => (
  <div className="ai-configuration-workspace">
    <ConfigurationSidebar steps={steps} activeStep={activeStep} onSelect={onSelectStep} />
    <div className="ai-step-content" id={`configuration-step-${activeStep}`} role="region" aria-label="Conteúdo da etapa atual">
      {children}
    </div>
  </div>
);

export const SelectableFeatureCard = ({
  title,
  description,
  dependency,
  icon: Icon,
  selected,
  onToggle,
  locked = false,
  lockedNote,
  dirty = false,
}: {
  title: string;
  description: string;
  dependency: string;
  icon: LucideIcon;
  selected: boolean;
  onToggle: () => void;
  locked?: boolean;
  lockedNote?: string;
  dirty?: boolean;
}) => (
  <button
    type="button"
    className={`ai-feature-card${selected ? ' is-selected' : ''}${locked ? ' is-locked' : ''}${dirty ? ' is-draft' : ''}`}
    aria-pressed={selected}
    aria-disabled={locked || undefined}
    onClick={locked ? undefined : onToggle}
  >
    <span className="ai-feature-icon" aria-hidden="true"><Icon size={22} /></span>
    <span className="ai-feature-check" aria-hidden="true">
      {selected ? <Check size={16} strokeWidth={3} /> : <Circle size={18} />}
    </span>
    <strong>
      {title}
      {dirty && <span className="ai-mode-card-draft-badge">Rascunho</span>}
    </strong>
    <span className="ai-feature-description">{description}</span>
    {locked && lockedNote ? (
      <small className="ai-feature-locked-note">{lockedNote}</small>
    ) : (
      <small>{dependency}</small>
    )}
  </button>
);

export type ModeSelectOption = {
  id: string;
  label: string;
  description: string;
  note?: string;
  disabled?: boolean;
  disabledNote?: string;
  action?: { label: string; onClick: () => void };
};

export const ModeOptionList = ({
  name,
  options,
  value,
  onSelect,
  size = 'compact',
}: {
  name: string;
  options: ModeSelectOption[];
  value: string | null;
  onSelect: (id: string) => void;
  size?: 'compact' | 'large';
}) => (
  <div className={`ai-mode-options is-${size}`} role="radiogroup" aria-label={name}>
    {options.map((option) => {
      const active = value === option.id;
      return (
        <div
          key={option.id}
          className={`ai-mode-option${active ? ' is-active' : ''}${option.disabled ? ' is-disabled' : ''}`}
        >
          <button
            type="button"
            role="radio"
            aria-checked={active}
            disabled={option.disabled}
            onClick={() => onSelect(option.id)}
          >
            <span className="ai-mode-option-marker" aria-hidden="true">
              {active ? <Check size={12} strokeWidth={4} /> : null}
            </span>
            <span className="ai-mode-option-copy">
              <strong>{option.label}</strong>
              <span>{option.description}</span>
              {option.note && <small>{option.note}</small>}
              {option.disabled && option.disabledNote && (
                <small className="is-warning">{option.disabledNote}</small>
              )}
            </span>
          </button>
          {option.disabled && option.action && (
            <button
              type="button"
              className="ai-button is-secondary ai-mode-option-action"
              onClick={option.action.onClick}
            >
              {option.action.label}
            </button>
          )}
        </div>
      );
    })}
  </div>
);

export const ModeFeatureCard = ({
  title,
  question,
  offNote,
  icon: Icon,
  selected,
  onToggle,
  options,
  value,
  onSelectMode,
  error,
  errorKey,
  dirty = false,
}: {
  title: string;
  question: string;
  offNote: string;
  icon: LucideIcon;
  selected: boolean;
  onToggle: () => void;
  options: ModeSelectOption[];
  value: string | null;
  onSelectMode: (id: string) => void;
  error?: string;
  errorKey?: string;
  dirty?: boolean;
}) => (
  <article
    className={`ai-feature-card ai-mode-card${selected ? ' is-selected' : ''}${error ? ' has-error' : ''}${dirty ? ' is-draft' : ''}`}
    data-error-key={errorKey}
  >
    <button type="button" className="ai-mode-card-toggle" aria-pressed={selected} onClick={onToggle}>
      <span className="ai-feature-icon" aria-hidden="true"><Icon size={22} /></span>
      <span className="ai-feature-check" aria-hidden="true">
        {selected ? <Check size={16} strokeWidth={3} /> : <Circle size={18} />}
      </span>
      <strong>
        {title}
        {dirty && <span className="ai-mode-card-draft-badge">Rascunho</span>}
      </strong>
      {!selected && <span className="ai-feature-description">{offNote}</span>}
    </button>
    {selected && (
      <div className="ai-mode-card-body">
        <ModeOptionList name={question} options={options} value={value} onSelect={onSelectMode} />
        {error && <p className="ai-mode-card-error">{error}</p>}
      </div>
    )}
  </article>
);

export const IntegrationCard = ({
  logo,
  logoClassName,
  theme,
  showStatus = true,
  name,
  status,
  description,
  actionLabel,
  onAction,
  children,
}: {
  logo: string;
  logoClassName?: string;
  theme?: 'reservo' | 'feedback-hero' | 'cv';
  showStatus?: boolean;
  name: string;
  status: 'connected' | 'disconnected';
  description: string;
  actionLabel: string;
  onAction: () => void;
  children?: ReactNode;
}) => (
  <article className={`ai-integration-card${theme ? ` is-themed is-${theme}-theme` : ''}`}>
    <img src={logo} className={logoClassName} alt="" aria-hidden="true" />
    <div className="ai-integration-copy">
      <div>
        <strong>{name}</strong>
        {showStatus && (
          <span className={`ai-integration-status is-${status}`}>
            {status === 'connected' ? 'Conectado' : 'Desconectado'}
          </span>
        )}
      </div>
      <p>{description}</p>
    </div>
    <button type="button" className="ai-button is-secondary" onClick={onAction}>{actionLabel}</button>
    {children && <div className="ai-integration-settings">{children}</div>}
  </article>
);

export type RoadmapStepItem = {
  id: string;
  title: string;
};

export type RoadmapRequirement = {
  label: string;
  done: boolean;
};

export const ValidationAlert = ({ message, onFix }: { message: string; onFix: () => void }) => (
  <div className="ai-validation-alert">
    <span><AlertCircle size={18} /> {message}</span>
    <button type="button" onClick={onFix}>Corrigir</button>
  </div>
);

export const BottomNavigation = ({
  canGoBack,
  isLastStep,
  busy,
  onBack,
  onContinue,
}: {
  canGoBack: boolean;
  isLastStep: boolean;
  busy?: boolean;
  onBack: () => void;
  onContinue: () => void;
}) => (
  <footer className="ai-bottom-navigation">
    <button type="button" className="ai-button is-secondary" onClick={onBack} disabled={!canGoBack}>
      <ArrowLeft size={17} /> Voltar
    </button>
    <div>
      <button type="button" className="ai-button is-primary" onClick={onContinue} disabled={busy}>
        {isLastStep ? (busy ? 'Publicando…' : 'Publicar atendimento') : 'Continuar'}
        {!isLastStep && <ArrowRight size={17} />}
      </button>
    </div>
  </footer>
);

export const ConversationTestDrawer = ({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ) || []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="ai-conversation-drawer-layer">
      <button type="button" className="ai-conversation-backdrop" onClick={onClose} aria-label="Fechar teste de conversa" />
      <aside ref={drawerRef} className="ai-conversation-drawer" role="dialog" aria-modal="true" aria-labelledby="conversation-test-title">
        <div className="ai-conversation-drawer-heading">
          <div>
            <h2 id="conversation-test-title">Testar conversa</h2>
            <p>Converse com o AtendaBot usando o rascunho atual.</p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
        </div>
        {children}
      </aside>
    </div>
  );
};
